import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../../../../middleware/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../context/AuthContext';
import MigrationPopup from '../../MigrationPopup';

const MIGRATION_POPUP_KEY = 'verbatim_migration_popup_shown';

const SignupForm = () => {
    const navigateTo = useNavigate();
    const { setUser } = useAuth();
    const [showMigrationPopup, setShowMigrationPopup] = useState(false);
    
    useEffect(() => {
        // Vérifier si le popup a déjà été affiché
        const popupShown = localStorage.getItem(MIGRATION_POPUP_KEY);
        if (!popupShown) {
            setShowMigrationPopup(true);
        }
    }, []);
    
    // Validation schema using Yup
    const validationSchema = Yup.object({
        firstname: Yup.string().required('Prénom requis'),
        lastname: Yup.string().required('Nom requis'),
        email: Yup.string().email('Email invalide').required('Email requis'),
        password: Yup.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').required('Mot de passe requis'),
    });

    const handleSignup = async (values, { setSubmitting }) => {
        try {
            // Envoyez une requête d'inscription avec les données utilisateur
            const response = await api.post('/auth/signup', values);
            if (response.status === 201) {
                const loginValues = {
                    email: values.email,
                    password: values.password
                }
                const loginResponse = await api.post('/auth/login', loginValues);
                const token = loginResponse.data.token;

                if (!token) {
                    throw new Error('Token non trouvé');
                } else {
                    // Store token in localStorage or cookie
                    localStorage.setItem('token', token);
                    try {
                        const user = await api.get('/users')
                        if (user.data) {
                            setUser(user.data)
                        }
                    } catch (error) {
                        console.error(error)
                    }
                }
            }
            // Affichez un message de réussite ou redirigez l'utilisateur vers la page de connexion
            setSubmitting(false);
            toast.success('Inscription réussie!');
            navigateTo('/');
        } catch (error) {
            // Gérez les erreurs d'inscription, par exemple : afficher un message d'erreur
            if (error.status === 400) {
                toast.error("Email déjà utilisé");
            } else {
                toast.error("Erreur d'inscription");
            }
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg">
            {showMigrationPopup && (
                <MigrationPopup onClose={() => setShowMigrationPopup(false)} />
            )}
            <h2 className="text-2xl font-bold text-gray-800 mb-8 relative">
                INSCRIPTION
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-primary -mb-3"></span>
            </h2>
            <Formik
                initialValues={{ firstname: '', lastname: '', email: '', password: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSignup}
            >
                {({ isSubmitting }) => (
                    <Form className="w-full flex flex-col items-center space-y-5">
                        <div className="grid grid-cols-2 gap-4 w-full">
                            <div className="w-full">
                                <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <Field
                                        type="text"
                                        name="firstname"
                                        className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                        placeholder="Jean"
                                    />
                                </div>
                                <ErrorMessage name="firstname" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                            
                            <div className="w-full">
                                <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <Field
                                        type="text"
                                        name="lastname"
                                        className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                        placeholder="Dupont"
                                    />
                                </div>
                                <ErrorMessage name="lastname" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                        </div>
                        
                        <div className="w-full">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                </div>
                                <Field
                                    type="email"
                                    name="email"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    placeholder="exemple@domaine.com"
                                />
                            </div>
                            <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                        </div>
                        
                        <div className="w-full">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <Field
                                    type="password"
                                    name="password"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    placeholder="••••••••"
                                />
                            </div>
                            <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                        </div>
                        
                        <div className="w-full pt-3 mt-2">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Inscription en cours...' : "S'inscrire"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default SignupForm;
